import { JobQueue } from '@st/util/job-queue'
import { Remote, wrap } from 'comlink'
import { DocumentStructure } from '../models'
import { MUPDF_LOADED, type MuPDFWorker } from '../workers/mupdf.worker'

type PDFViewerState = {
  status: 'loading' | 'loaded'
  url: string
  documentStructure: DocumentStructure
}

const TARGET_DPI = 96 * 2
const RENDER_CONCURRENCY = 2

// Arguments for the worker function
type RenderPageArgs = [pageNumber: number, scale: number]

export class MuPDFRendererBackend {
  private worker: Worker
  private comlink: Remote<MuPDFWorker>
  private jobQueue: JobQueue<RenderPageArgs, ImageBitmap>

  private _state: PDFViewerState
  private listeners: Set<() => void> = new Set()

  private devicePixelRatio: number

  constructor({ documentUrl }: { documentUrl: string }) {
    this._state = {
      status: 'loading',
      url: documentUrl,
      documentStructure: { pages: [] }
    }

    this.devicePixelRatio = window.devicePixelRatio

    // Initialize JobQueue with concurrency
    this.jobQueue = new JobQueue({
      maxConcurrency: RENDER_CONCURRENCY,
      onEvent(_event) {}
    })

    this.worker = new Worker(new URL('../workers/mupdf.worker', import.meta.url), {
      type: 'module'
    })
    this.comlink = wrap<MuPDFWorker>(this.worker)

    this.worker.addEventListener('message', (event) => {
      if (event.data === MUPDF_LOADED) {
        fetch(this._state.url)
          .then((response) => response.arrayBuffer())
          .then((arrayBuffer) => this.comlink.loadDocument(arrayBuffer))
          .then((documentStructure) => {
            this.setState({
              status: 'loaded',
              url: this._state.url,
              documentStructure: documentStructure
            })
          })
          .catch((error) => {
            console.error('Failed to load document:', error)
            // Consider updating state to 'error'
          })
      }
    })
  }

  renderPage(
    pageNumber: number,
    { priority = 0 }: { priority?: number } = {}
  ): Promise<ImageBitmap> {
    if (this._state.status != 'loaded') {
      throw new Error('RenderPage called before document loaded')
    }

    const scale = (this.devicePixelRatio * TARGET_DPI) / 72

    const renderAndGetBitmap = async (pageNum: number, scale: number): Promise<ImageBitmap> => {
      return await this.comlink.renderPageAsImage(pageNum, scale)
    }

    const task = this.jobQueue.enqueue({
      func: renderAndGetBitmap,
      args: [pageNumber, scale],
      priority: priority
    })

    return task.promise
  }

  cancelRender(pageNumber: number) {
    this.jobQueue.cancel((task) => task.args[0] === pageNumber)
  }

  getState(): PDFViewerState {
    return this._state
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private setState(newState: PDFViewerState) {
    if (newState !== this._state) {
      this._state = newState
      this.listeners.forEach((listener) => listener())
    }
  }

  dispose() {
    this.jobQueue.clear()
    this.worker.terminate()
    this.listeners.clear()
  }
}
